import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Language, TenantLegalDocuments, VisitingCardPayload } from '../types';
import { api } from '../lib/api';
import { _t, detectInitialLanguage, ensureLanguage, persistPreferredLanguage } from '../lib/i18n';
import { applyTenantThemeCss, clearTenantThemeCss } from '../lib/tenantThemeCss';
import { normalizeTenantTheme } from '../lib/tenantTheme';
import { VisitingCardPhoneFrame } from '../components/visitingCard/VisitingCardPhoneFrame';
import { VisitingCardCanvas } from '../components/visitingCard/VisitingCardCanvas';
import { VisitingCardLoginScreen } from '../components/visitingCard/VisitingCardLoginScreen';
import { InstallPWA } from '../components/common/InstallPWA';
import { CookieConsentBanner } from '../components/legal/CookieConsentBanner';
import { connectStorefrontSocket, disconnectStorefrontSocket } from '../lib/storefrontSocket';
import { VISITING_CARD_UPDATED_EVENT } from '../lib/visitingCardEvents';
import { STOREFRONT_UPDATED_EVENT } from '../lib/storefrontEvents';
import {
  PullToRefreshProvider,
  useRegisterPullToRefresh,
} from '../context/PullToRefreshContext';
import { useStorefrontAuth } from '../hooks/useStorefrontAuth';
import {
  headerPillPrimaryClass,
  visitingCardPullToRefreshIndicatorClass,
} from '../styles/uiStandards';

const STOREFRONT_MANIFEST = '/manifest.webmanifest';
const CARD_MANIFEST = '/c/manifest.webmanifest';
const CARD_PWA_DISMISS_KEY = 'catalogofworks_pwa_visiting_card_dismissed';
const CARD_SCROLL_SELECTOR = '.visiting-card-scroll';

/** Shared chrome pills: ~1.5× the prior compact height (~16px → 24px); soft lift. */
const cardChromePillClass =
  'inline-flex h-6 items-center justify-center rounded-full px-2.5 text-[10px] font-bold uppercase leading-none tracking-wide shadow-[0_1px_2px_rgba(0,0,0,0.2),0_2px_8px_rgba(0,0,0,0.12)]';
const cardChromeAuthClass =
  `${cardChromePillClass} border border-transparent bg-[var(--accent-primary)] text-[var(--accent-fg,#fff)] transition-[filter] hover:brightness-95`;

/**
 * Horizontal inset matches sheet photo column (`px-4` on `.visiting-card-scroll`).
 * Vertical: room under chrome pills (40px) so buttons don’t sit on the photo.
 */
const cardChromeStripClass =
  'absolute inset-x-0 top-0 z-30 flex items-center justify-between gap-2 px-4 pt-2';
const cardChromeContentPadClass = 'h-full pt-10';

type VisitingCardPublicPageProps = {
  /** Tenant legal overrides from App tenant context (cookie banner). */
  legalDocuments?: TenantLegalDocuments | null;
};

function VisitingCardPullRefreshBridge({
  onRefresh,
  disabled,
}: {
  onRefresh: () => Promise<void>;
  disabled: boolean;
}) {
  useRegisterPullToRefresh(disabled ? null : onRefresh);
  return null;
}

export default function VisitingCardPublicPage({
  legalDocuments = null,
}: VisitingCardPublicPageProps) {
  const [params] = useSearchParams();
  const [lang, setLang] = useState<Language>(() => detectInitialLanguage());
  const [card, setCard] = useState<VisitingCardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const hasLoadedRef = useRef(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const tenantSlug = card?.tenant?.slug || '';
  const { isGuest, logout } = useStorefrontAuth(tenantSlug || null);

  useEffect(() => {
    persistPreferredLanguage(lang);
  }, [lang]);

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return undefined;
    const previous = link.getAttribute('href') || STOREFRONT_MANIFEST;
    link.setAttribute('href', CARD_MANIFEST);
    return () => {
      link.setAttribute('href', previous.includes('/c/') ? STOREFRONT_MANIFEST : previous);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only the very first mount shows the loader: a language switch or a live update
      // must never blank a card that is already on screen.
      const firstLoad = !hasLoadedRef.current;
      hasLoadedRef.current = true;
      if (firstLoad) {
        setLoading(true);
        setError(null);
      }
      try {
        const data = await api.getPublicVisitingCard(lang);
        if (!cancelled) {
          setCard(data.card);
          setError(null);
        }
      } catch {
        if (!cancelled && firstLoad) {
          setCard(null);
          setError('not_found');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang, params, reloadKey]);

  // Owner saved new settings (or theme/BIO/contacts) — pull a fresh card into every open one.
  const tenantId = card?.tenant?.id || null;
  useEffect(() => {
    if (!tenantId) return undefined;
    connectStorefrontSocket(tenantId);
    const onUpdated = () => setReloadKey((v) => v + 1);
    window.addEventListener(VISITING_CARD_UPDATED_EVENT, onUpdated);
    window.addEventListener(STOREFRONT_UPDATED_EVENT, onUpdated);
    return () => {
      window.removeEventListener(VISITING_CARD_UPDATED_EVENT, onUpdated);
      window.removeEventListener(STOREFRONT_UPDATED_EVENT, onUpdated);
      disconnectStorefrontSocket();
    };
  }, [tenantId]);

  useEffect(() => {
    if (!card?.theme) {
      clearTenantThemeCss();
      return;
    }
    applyTenantThemeCss(normalizeTenantTheme(card.theme));
    return () => {
      clearTenantThemeCss();
    };
  }, [card?.theme]);

  // Soft API reload is not enough after a deploy: the shell keeps the old JS bundle
  // (BIO align, layout, …). PTR is an explicit user gesture — reload the whole page.
  const refreshCard = useCallback(async () => {
    window.location.reload();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-stone-950 text-stone-300">
        {_t('common.loading', lang)}
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-stone-950 px-4 text-center text-stone-300">
        <p>{_t('visitingCard.notFound', lang)}</p>
        <div className="flex gap-2">
          {(['ru', 'en', 'he'] as Language[]).map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => void ensureLanguage(code).then(() => setLang(code))}
              className="rounded-full border border-stone-600 px-3 py-1 text-xs uppercase"
            >
              {code}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const installName = card.config?.pwaShortcutName || card.tenant?.name || undefined;

  return (
    <PullToRefreshProvider
      lang={lang}
      enabled={!previewOpen && !loginOpen}
      scrollContainerSelector={CARD_SCROLL_SELECTOR}
      indicatorClassName={visitingCardPullToRefreshIndicatorClass}
    >
      <VisitingCardPullRefreshBridge
        onRefresh={refreshCard}
        disabled={previewOpen || loginOpen}
      />
      <VisitingCardPhoneFrame>
        <div className="relative h-full w-full">
          {/*
            Fixed strip so layout chrome (flip toggle, etc.) never sits under lang chips.
            Hidden only in the photo viewer. Login keeps lang chips (auth form has its own back).
          */}
          <div
            className={[
              cardChromeStripClass,
              previewOpen ? 'hidden' : '',
            ].join(' ')}
          >
            {loginOpen ? (
              <span className="w-[4.5rem]" aria-hidden />
            ) : isGuest ? (
              <button
                type="button"
                id="visiting-card-btn-login"
                className={cardChromeAuthClass}
                onClick={() => {
                  setLogoutConfirm(false);
                  setLoginOpen(true);
                }}
              >
                {_t('common.login', lang)}
              </button>
            ) : (
              <button
                type="button"
                id="visiting-card-btn-logout"
                className={cardChromeAuthClass}
                onClick={() => setLogoutConfirm(true)}
              >
                {_t('common.logout', lang)}
              </button>
            )}
            <div className="flex gap-1">
              {(['ru', 'en', 'he'] as Language[]).map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    void ensureLanguage(code).then(() => {
                      setLang(code);
                      persistPreferredLanguage(code);
                    });
                  }}
                  className={[
                    cardChromePillClass,
                    code === lang
                      ? 'bg-[var(--accent-primary)] text-[var(--accent-fg)]'
                      : 'bg-white/80 text-stone-700',
                  ].join(' ')}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>

          {logoutConfirm && !loginOpen ? (
            <div className="absolute inset-x-4 top-11 z-40 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary,#fff)] p-3 shadow-lg">
              <p className="text-sm text-[var(--text-primary)]">
                {_t('common.logoutConfirm', lang)}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  className={`min-w-0 flex-1 ${headerPillPrimaryClass}`}
                  onClick={() => {
                    setLogoutConfirm(false);
                    void logout();
                  }}
                >
                  {_t('common.logout', lang)}
                </button>
                <button
                  type="button"
                  className="min-w-0 flex-1 rounded-full border border-[var(--border-color)] bg-white/90 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-stone-700"
                  onClick={() => setLogoutConfirm(false)}
                >
                  {_t('common.cancel', lang)}
                </button>
              </div>
            </div>
          ) : null}

          <div className={previewOpen ? 'h-full' : cardChromeContentPadClass}>
            {loginOpen ? (
              <VisitingCardLoginScreen
                lang={lang}
                tenantSlug={card.tenant.slug}
                tenantName={card.tenant.name}
                onBack={() => setLoginOpen(false)}
                onLoggedIn={() => setLoginOpen(false)}
              />
            ) : (
              <VisitingCardCanvas
                lang={lang}
                card={card}
                onPreviewOpenChange={setPreviewOpen}
              />
            )}
          </div>
        </div>
      </VisitingCardPhoneFrame>
      <InstallPWA
        lang={lang}
        appName={installName}
        storageKey={CARD_PWA_DISMISS_KEY}
      />
      <CookieConsentBanner
        key={`cookie-${lang}`}
        lang={lang}
        tenantSlug={card.tenant.slug}
        legalDocuments={legalDocuments}
      />
    </PullToRefreshProvider>
  );
}
